import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { HEADER_CHECKBOX_HIT_AREA } from '@/components/documents/document-grid'
import { Checkbox } from '@/components/ui/checkbox'
import { GridTableHeader } from '@/components/ui/grid-table'
import type { CaseDocumentSort } from '@/services/api/cases'
import { cn } from '@/utils/cn'

export interface DocumentTableHeaderProps {
  selectAllChecked: boolean
  selectAllIndeterminate: boolean
  onSelectAll: () => void
  /** The files' order and the change of it, by the two time columns. */
  sorting: { sort: CaseDocumentSort; onSort: (by: CaseDocumentSort['by']) => void }
}

export function DocumentTableHeader({
  selectAllChecked,
  selectAllIndeterminate,
  onSelectAll,
  sorting,
}: DocumentTableHeaderProps) {
  const { t } = useTranslation()
  return (
    <GridTableHeader>
      <div className={HEADER_CHECKBOX_HIT_AREA} onClick={onSelectAll}>
        <Checkbox
          checked={selectAllChecked}
          {...(selectAllIndeterminate ? { 'data-state': 'indeterminate' } : {})}
        />
      </div>
      <div>{t('documents.columns.name')}</div>
      {/* The phone shows these on a second line under the name (see DocumentRow). */}
      <div className="max-md:hidden">{t('documents.detail.type')}</div>
      <div className="max-md:hidden">{t('documents.detail.size')}</div>
      <div className="max-md:hidden">{t('documents.columns.status')}</div>
      <div className="max-md:hidden">{t('documents.detail.received')}</div>
      <SortableHeader by="uploaded" label={t('documents.detail.uploaded')} sorting={sorting} />
      <SortableHeader by="processed" label={t('documents.detail.processed')} sorting={sorting} />
      <div />
    </GridTableHeader>
  )
}

interface SortableHeaderProps {
  by: CaseDocumentSort['by']
  label: string
  sorting: DocumentTableHeaderProps['sorting']
}

/** A time column's label, which sorts the files by that time (see `nextSort`). */
function SortableHeader({ by, label, sorting }: SortableHeaderProps) {
  const { t } = useTranslation()
  const active = sorting.sort.by === by
  const descending = sorting.sort.order === 'desc'
  const Icon = !active ? ArrowUpDown : descending ? ArrowDown : ArrowUp
  const description = !active
    ? t('documents.columns.sortBy', { column: label })
    : t(descending ? 'documents.columns.newestFirst' : 'documents.columns.oldestFirst', {
        column: label,
      })
  return (
    <div className="max-md:hidden">
      <button
        type="button"
        title={description}
        onClick={() => sorting.onSort(by)}
        className={cn(
          'group/sort -mx-1 inline-flex cursor-pointer items-center gap-1 rounded px-1 uppercase tracking-wider transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active && 'text-foreground'
        )}
      >
        {label}
        <Icon
          aria-hidden
          className={cn(
            'size-3 shrink-0',
            !active && 'opacity-40 transition-opacity group-hover/sort:opacity-100'
          )}
        />
      </button>
    </div>
  )
}
